import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { 
  Customer, 
  CustomerProfile, 
  CustomerFormData,
  RegionFormData,
  SiteFormData,
  UserFormData,
  SitesByRegion,
  CompleteCustomerData,
  FormStep,
  FormMode,
  ValidationErrors
} from '../models';

/**
 * State management service for customer onboarding/editing forms
 * Manages form state, validation, and data flow between components
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerStateService {

  // Core data streams
  private _customer$ = new BehaviorSubject<Customer | null>(null);
  private _customerProfile$ = new BehaviorSubject<CustomerProfile | null>(null);
  private _regions$ = new BehaviorSubject<RegionFormData[]>([]);
  private _sites$ = new BehaviorSubject<SiteFormData[]>([]);
  private _adminUsers$ = new BehaviorSubject<UserFormData[]>([]);
  private _operatorUsers$ = new BehaviorSubject<UserFormData[]>([]);

  // Form state streams
  private _formMode$ = new BehaviorSubject<FormMode>(FormMode.CREATE);
  private _currentStep$ = new BehaviorSubject<FormStep>(FormStep.CUSTOMER_INFO);
  private _isLoading$ = new BehaviorSubject<boolean>(false);
  private _isSubmitting$ = new BehaviorSubject<boolean>(false);
  private _hasUnsavedChanges$ = new BehaviorSubject<boolean>(false);
  private _validationErrors$ = new BehaviorSubject<ValidationErrors>({});

  constructor() {}

  // Public observables
  get customer$(): Observable<Customer | null> { return this._customer$.asObservable(); }
  get customerProfile$(): Observable<CustomerProfile | null> { return this._customerProfile$.asObservable(); }
  get regions$(): Observable<RegionFormData[]> { return this._regions$.asObservable(); }
  get sites$(): Observable<SiteFormData[]> { return this._sites$.asObservable(); }
  get adminUsers$(): Observable<UserFormData[]> { return this._adminUsers$.asObservable(); }
  get operatorUsers$(): Observable<UserFormData[]> { return this._operatorUsers$.asObservable(); }
  
  get formMode$(): Observable<FormMode> { return this._formMode$.asObservable(); }
  get currentStep$(): Observable<FormStep> { return this._currentStep$.asObservable(); }
  get isLoading$(): Observable<boolean> { return this._isLoading$.asObservable(); }
  get isSubmitting$(): Observable<boolean> { return this._isSubmitting$.asObservable(); }
  get hasUnsavedChanges$(): Observable<boolean> { return this._hasUnsavedChanges$.asObservable(); }
  get validationErrors$(): Observable<ValidationErrors> { return this._validationErrors$.asObservable(); }

  // Computed observables
  get sitesByRegion$(): Observable<SitesByRegion[]> {
    return combineLatest(this.regions$, this.sites$).pipe(
      map((data: any[]) => {
        const [regions, sites] = data;
        return regions.map((region: RegionFormData) => ({
          regionId: region.id || 0,
          regionName: region.name,
          sites: sites.filter((site: SiteFormData) => site.region_id === region.id)
        }));
      })
    );
  }

  get completeFormData$(): Observable<CustomerFormData> {
    return combineLatest(
      this.customer$,
      this.customerProfile$,
      this.regions$,
      this.sites$,
      this.adminUsers$,
      this.operatorUsers$,
      this.formMode$,
      this.currentStep$,
      this.hasUnsavedChanges$,
      this.validationErrors$
    ).pipe(
      map((data: any[]) => {
        const [customer, customerProfile, regions, sites, adminUsers, operatorUsers, formMode, currentStep, hasUnsavedChanges, validationErrors] = data;
        const formData: CustomerFormData = {
          customer: customer || { name: '', contactNo: '', email: '' },
          customerProfile: customerProfile || { name: '' },
          regions: regions || [],
          sites: sites || [],
          adminUsers: adminUsers || [],
          operatorUsers: operatorUsers || [],
          isEditMode: formMode === FormMode.EDIT,
          customerId: customer ? customer.id : undefined,
          currentStep: currentStep || FormStep.CUSTOMER_INFO,
          hasUnsavedChanges: hasUnsavedChanges || false,
          isValid: !validationErrors || Object.keys(validationErrors).length === 0,
          validationErrors: validationErrors || {}
        };
        return formData;
      })
    );
  }

  // Customer methods
  setCustomer(customer: Customer): void {
    this._customer$.next(customer);
    this.markAsModified();
  }

  updateCustomer(updates: Partial<Customer>): void {
    const current = this._customer$.value;
    if (current) {
      this._customer$.next({ ...current, ...updates });
      this.markAsModified();
    }
  }

  setCustomerProfile(profile: CustomerProfile): void {
    this._customerProfile$.next(profile);
    this.markAsModified();
  }

  updateCustomerProfile(updates: Partial<CustomerProfile>): void {
    const current = this._customerProfile$.value;
    if (current) {
      this._customerProfile$.next({ ...current, ...updates });
      this.markAsModified();
    }
  }

  // Region methods
  setRegions(regions: RegionFormData[]): void {
    this._regions$.next(regions);
    this.markAsModified();
  }

  addRegion(region: RegionFormData): void {
    const current = this._regions$.value;
    this._regions$.next([...current, { ...region, isNew: true }]);
    this.markAsModified();
  }

  updateRegion(index: number, updates: Partial<RegionFormData>): void {
    const current = this._regions$.value;
    const updated = current.map((region, i) => 
      i === index ? { ...region, ...updates, hasUnsavedChanges: true } : region
    );
    this._regions$.next(updated);
    this.markAsModified();
  }

  removeRegion(index: number): void {
    const current = this._regions$.value;
    const regionToRemove = current[index];
    
    // Also remove sites associated with this region
    if (regionToRemove.id) {
      this.removeSitesByRegionId(regionToRemove.id);
    }
    
    const updated = current.filter((_, i) => i !== index);
    this._regions$.next(updated);
    this.markAsModified();
  }

  // Site methods
  setSites(sites: SiteFormData[]): void {
    this._sites$.next(sites);
    this.markAsModified();
  }

  addSite(site: SiteFormData): void {
    const current = this._sites$.value;
    this._sites$.next([...current, { ...site, isNew: true }]);
    this.markAsModified();
  }

  updateSite(index: number, updates: Partial<SiteFormData>): void {
    const current = this._sites$.value;
    const updated = current.map((site, i) => 
      i === index ? { ...site, ...updates, hasUnsavedChanges: true } : site
    );
    this._sites$.next(updated);
    this.markAsModified();
  }

  removeSite(index: number): void {
    const current = this._sites$.value;
    const updated = current.filter((_, i) => i !== index);
    this._sites$.next(updated);
    this.markAsModified();
  }

  private removeSitesByRegionId(regionId: number | string): void {
    const current = this._sites$.value;
    const updated = current.filter(site => String(site.region_id) !== String(regionId));
    this._sites$.next(updated);
  }

  // User methods
  setAdminUsers(users: UserFormData[]): void {
    this._adminUsers$.next(users);
    this.markAsModified();
  }

  addAdminUser(user: UserFormData): void {
    const current = this._adminUsers$.value;
    this._adminUsers$.next([...current, { ...user, userType: 'admin', isNew: true }]);
    this.markAsModified();
  }

  updateAdminUser(index: number, updates: Partial<UserFormData>): void {
    const current = this._adminUsers$.value;
    const updated = current.map((user, i) => 
      i === index ? { ...user, ...updates, hasUnsavedChanges: true } : user
    );
    this._adminUsers$.next(updated);
    this.markAsModified();
  }

  removeAdminUser(index: number): void {
    const current = this._adminUsers$.value;
    const updated = current.filter((_, i) => i !== index);
    this._adminUsers$.next(updated);
    this.markAsModified();
  }

  setOperatorUsers(users: UserFormData[]): void {
    this._operatorUsers$.next(users);
    this.markAsModified();
  }

  addOperatorUser(user: UserFormData): void {
    const current = this._operatorUsers$.value;
    this._operatorUsers$.next([...current, { ...user, userType: 'operator', isNew: true }]);
    this.markAsModified();
  }

  updateOperatorUser(index: number, updates: Partial<UserFormData>): void {
    const current = this._operatorUsers$.value;
    const updated = current.map((user, i) => 
      i === index ? { ...user, ...updates, hasUnsavedChanges: true } : user
    );
    this._operatorUsers$.next(updated);
    this.markAsModified();
  }

  removeOperatorUser(index: number): void {
    const current = this._operatorUsers$.value;
    const updated = current.filter((_, i) => i !== index);
    this._operatorUsers$.next(updated);
    this.markAsModified();
  }

  // Form state methods
  setFormMode(mode: FormMode): void {
    this._formMode$.next(mode);
  }

  setCurrentStep(step: FormStep): void {
    this._currentStep$.next(step);
  }

  setLoading(loading: boolean): void {
    this._isLoading$.next(loading);
  }

  setSubmitting(submitting: boolean): void {
    this._isSubmitting$.next(submitting);
  }

  setValidationErrors(errors: ValidationErrors): void {
    this._validationErrors$.next(errors);
  }

  private markAsModified(): void {
    this._hasUnsavedChanges$.next(true);
  }


  loadCompleteCustomerData(data: CompleteCustomerData): void {
    this._customer$.next(data.customer);
    this._customerProfile$.next(data.customerProfile);
    
    // Convert regions to form data
    const regionsFormData: RegionFormData[] = data.regions.map(region => ({
      id: region.id,
      name: region.name,
      isNew: false,
      hasUnsavedChanges: false,
      lastSavedValue: region.name,
      autoSaveStatus: 'saved'
    }));
    this._regions$.next(regionsFormData);

    // Convert sites to form data  
    const sitesFormData: SiteFormData[] = data.sites.map(site => {
      const region = data.regions.find(r => r.id === site.region_id);
      return {
        id: site.id,
        name: site.name,
        region_id: site.region_id,
        customer_id: site.customer_id,
        version: site.version,
        created_on: site.created_on,
        regionName: region ? region.name : '',
        isNew: false,
        hasUnsavedChanges: false,
        lastSavedValue: site.name,
        autoSaveStatus: 'saved'
      };
    });
    this._sites$.next(sitesFormData);

    // Convert users to form data
    const adminUsers: UserFormData[] = [];
    const operatorUsers: UserFormData[] = [];

    data.users.forEach(userWithRelations => {
      const isAdmin = userWithRelations.roles.some(role => role.name === 'admin');
      const userFormData: UserFormData = {
        id: userWithRelations.user.id,
        username: userWithRelations.user.username,
        userDetails: userWithRelations.userDetails,
        roleIds: userWithRelations.roles.map(role => role.id || 0),
        roleNames: userWithRelations.roles.map(role => role.name),
        regionIds: userWithRelations.regions.map(region => region.id || 0),
        regionNames: userWithRelations.regions.map(region => region.name || ''),
        siteIds: userWithRelations.sites.map(site => site.id || 0),
        siteNames: userWithRelations.sites.map(site => site.name || ''),
        userType: isAdmin ? 'admin' : 'operator',
        isNew: false
      };

      if (isAdmin) {
        adminUsers.push(userFormData);
      } else {
        operatorUsers.push(userFormData);
      }
    });

    this._adminUsers$.next(adminUsers);
    this._operatorUsers$.next(operatorUsers);
    this._hasUnsavedChanges$.next(false);
  }



  // Validation helpers
  isStepValid(step: FormStep): boolean {
    const errors = this._validationErrors$.value;
    
    switch (step) {
      case FormStep.CUSTOMER_INFO:
        return !errors.customer && !errors.customerProfile;
      case FormStep.REGIONS:
        return !errors.regions && this._regions$.value.length > 0;
      case FormStep.SITES:
        return !errors.sites && this._sites$.value.length > 0;
      case FormStep.USERS:
        return !errors.adminUsers && !errors.operatorUsers && this._adminUsers$.value.length > 0;
      default:
        return false;
    }
  }

  canProceedToStep(step: FormStep): boolean {
    // Check if all previous steps are valid
    for (let i = 1; i < step; i++) {
      if (!this.isStepValid(i as FormStep)) {
        return false;
      }
    }
    return true;
  }
}